<template>
  <div class="verification-page">
    <!-- Header Section -->
    <section class="header">
      <h1>Pre-Verification Form</h1>

      <ul>
        <li>
          It is crucial that you input all the details correctly, so please
          spend the time to read the
          <a href="https://mywhoosh.com/ruleset" target="_blank"
            >technical guide and ruleset</a
          >
          and be as thorough as possible.
        </li>
        <li>
          All videos must now be submitted as an unlisted YouTube link. No
          direct file submission will be accepted.
        </li>
      </ul>
      <p>
        For any issues with this form, please contact
        <a href="mailto:racecontrol@mywhoosh.com">racecontrol@mywhoosh.com</a>.
      </p>
    </section>

    <!-- Steps -->
    <section class="steps-box">
      <!-- Loading state -->
      <div v-if="loading" class="loading-indicator">
        <p>Submitting your information...</p>
      </div>

      <form @submit.prevent="validateAndSubmit">
        <template v-if="!submitted">
          <!-- Step 1: Hardware -->
          <div class="form-section">
            <h3>1. Hardware Details</h3>

            <label for="smartTrainer">Primary Power Source</label>
            <select
              v-model="form.smartTrainer"
              :class="{ 'input-error': validationErrors.smartTrainer }"
            >
              <option disabled value="">Select Primary Power Source</option>
              <option>Wahoo KICKR V5</option>
              <option>Wahoo KICKR V6</option>
              <option>Wahoo KICKR Move</option>
              <option>Wahoo KICKR Bike V1</option>
              <option>Wahoo KICKR Bike V2</option>
              <option>Wahoo KICKR Shift</option>
              <option>Wahoo KICKR Core</option>
              <option>Wahoo KICKR Core 2</option>
              <option>Tacx Neo</option>
              <option>Tacx Neo 2T</option>
              <option>Tacx Neo 3M</option>
              <option>Tacx Neo Bike</option>
              <option>Tacx Neo Bike Plus</option>
              <option>Elite Justo</option>
              <option>Elite Justo 2</option>
              <option>Jet Black Victory</option>
              <option>Elite Justo Avanti</option>
            </select>
            <span v-if="validationErrors.smartTrainer" class="error-text">{{
              validationErrors.smartTrainer
            }}</span>

            <label for="secondaryPowerSource">Secondary Power Source</label>
            <input
              type="text"
              v-model="form.secondaryPowerSource"
              placeholder="Include make, model, serial number of secondary power source (pedals, crank, etc.)"
              :class="{ 'input-error': validationErrors.secondaryPowerSource }"
            />
            <span
              v-if="validationErrors.secondaryPowerSource"
              class="error-text"
            >
              {{ validationErrors.secondaryPowerSource }}
            </span>
          </div>

          <!-- Step 2: Weight/Height -->
          <div class="form-section">
            <h3>2. Share your Weight and Height Details</h3>
            <p class="step-description">
              Please note that the weight and height must match the weight and
              height in your videos.
            </p>

            <label for="weight">Weight (kg)</label>
            <input
              type="number"
              step="0.1"
              v-model.number="form.weight"
              placeholder="Weight (kg)"
              :class="{ 'input-error': validationErrors.weight }"
            />
            <span v-if="validationErrors.weight" class="error-text">{{
              validationErrors.weight
            }}</span>

            <label for="height">Height (cm)</label>
            <input
              type="number"
              v-model.number="form.height"
              placeholder="Height (cm)"
              :class="{ 'input-error': validationErrors.height }"
            />
            <span v-if="validationErrors.height" class="error-text">{{
              validationErrors.height
            }}</span>
          </div>

          <!-- Step 3: Video -->
          <div class="form-section">
            <h3>
              3. Share video proof for Smart Trainer, Height, Weight, Secondary
              Power Source and Power Passport Workout
            </h3>
            <p class="step-description">
              The accepted protocol for power passport text, weigh-in, height
              and equipment videos can be found in the
              <a href="https://mywhoosh.com/ruleset" target="_blank"
                >MyWhoosh ruleset</a
              >
            </p>

            <label for="videoLink">Video Link</label>
            <input
              type="text"
              v-model="form.videoLink"
              placeholder="https://www.youtube.com/..."
              :class="{ 'input-error': validationErrors.videoLink }"
            />
            <span v-if="validationErrors.videoLink" class="error-text">{{
              validationErrors.videoLink
            }}</span>
          </div>

          <!-- Step 4: Strava -->
          <div class="form-section">
            <h3>4. Strava link</h3>

            <input
              type="text"
              v-model="form.stravaLink"
              placeholder="Your public strava link"
              :class="{ 'input-error': validationErrors.stravaLink }"
            />
            <span v-if="validationErrors.stravaLink" class="error-text">{{
              validationErrors.stravaLink
            }}</span>
          </div>

          <!-- Step 5: Message -->
          <div class="form-section">
            <h3>5. Message</h3>

            <textarea
              v-model="form.message"
              placeholder="Any message..."
            ></textarea>
          </div>

          <!-- Info Note -->
          <p class="note">
            ⚠ Please note that the verification process for all steps is
            expected to be completed within 72 hours.
          </p>

          <!-- Error display -->
          <div v-if="errors.length" class="error-messages">
            <h4>Please correct the following errors:</h4>
            <ul>
              <li
                v-for="(error, index) in errors"
                :key="index"
                class="error-item"
              >
                {{ error }}
              </li>
            </ul>
          </div>

          <!-- Buttons -->
          <div class="form-actions justify-end">
            <button
              style="display: none"
              type="button"
              @click="cancel"
              class="btn-cancel"
              :disabled="loading"
            >
              Cancel
            </button>
            <button type="submit" class="btn-submit" :disabled="loading">
              Submit
            </button>
          </div>
        </template>
      </form>

      <transition name="fade">
        <template v-if="submitted">
          <div class="success-message">
            <h3>Thank you for your submission!</h3>
            <p>
              Your verification request has been received. We will review it
              within the next 12 hours.
            </p>
          </div>
        </template>
      </transition>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ValidationMixin from "@/mixins/src-event/ValidationMixin";

export default {
  name: "VerificationPage",
  mixins: [ValidationMixin],
  head: {
    title: {
      inner: "Pre-Verification Form - MyWhoosh",
    },
    link: [
      {
        rel: "stylesheet",
        href: "/assets/css/src_event.css",
      },
    ],
  },
  data() {
    return {
      form: {
        smartTrainer: "",
        secondaryPowerSource: "",
        weight: "",
        height: "",
        videoLink: "",
        stravaLink: "",
        message: "",
      },
      validationErrors: {},
      errors: [],
      loading: false,
      submitted: false,
      verificationStatus: null,
    };
  },
  computed: {
    ...mapGetters("src_verification", [
      "verificationData",
      "verificationError",
    ]),
    ...mapGetters("user", {
      verification_status: "verification_status",
    }),
  },
  created() {
    this.checkUserAndVerificationStatus();
  },
  methods: {
    ...mapActions("src_verification", [
      "saveVerificationData",
      "resetVerification",
    ]),

    ...mapActions("user", [
      "get_rider_default_data", // Add this to refresh data after submission
    ]),

    checkUserAndVerificationStatus() {
      try {
        // Check for login token in localStorage
        const token = localStorage.getItem("webToken");
        if (!token) {
          this.safeNavigate("/login");
          return;
        }

        // Use Vuex getter instead of localStorage
        this.verificationStatus = this.verification_status;

        // Allow access for 'not_submitted' and 'rejected' statuses
        if (this.verificationStatus === "pending") {
          this.safeNavigate("/user/activities#profile");
        } else if (this.verificationStatus === "approved") {
          this.safeNavigate("/user/activities#profile");
        }
        // Allow 'rejected' and 'not_submitted' users to access the form
      } catch (error) {
        this.safeNavigate("/login");
      }
    },

    // Helper method to safely navigate
    safeNavigate(route) {
      const currentPath = this.$route.path + (this.$route.hash || "");
      if (currentPath !== route) {
        this.$router.push(route).catch((err) => {
          // Ignore navigation duplicated errors
          if (err.name !== "NavigationDuplicated") {
            console.error("Navigation error:", err);
          }
        });
      }
    },

    async validateAndSubmit() {
      if (!this.validateForm()) return;

      this.loading = true;
      try {
        const response = await this.saveVerificationData(this.form);

        const status = response?.data?.status;
        const message = response?.data?.message;

        if (status) {
          this.submitted = true;
          await this.get_rider_default_data();

          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });

          setTimeout(() => {
            this.safeNavigate("/user/activities#profile");
          }, 3000);
        } else {
          this.errors = [
            message || "There was a problem submitting your data.",
          ];
        }
      } catch (error) {
        this.handleValidationErrors(error, this.validationErrors, this.errors);
      } finally {
        this.loading = false;
      }
    },

    cancel() {
      const hasData = Object.values(this.form).some((value) => !!value);

      if (hasData) {
        const confirmCancel = window.confirm(
          "Are you sure you want to cancel? All your entered data will be lost."
        );
        if (confirmCancel) {
          this.resetForm();
          this.$router.go(-1);
        }
      } else {
        this.$router.go(-1);
      }
    },

    resetForm() {
      this.form = {
        smartTrainer: "",
        secondaryPowerSource: "",
        weight: "",
        height: "",
        videoLink: "",
        stravaLink: "",
        message: "",
      };
      this.validationErrors = {};
      this.errors = [];
      this.resetVerification();
    },
  },
  beforeDestroy() {
    if (!this.submitted) {
      this.resetVerification();
    }
  },
  watch: {
    verification_status: {
      handler(newStatus, oldStatus) {
        // Only act if status actually changed
        if (newStatus !== oldStatus) {
          this.verificationStatus = newStatus;
          if (newStatus !== "not_submitted") {
            this.safeNavigate("/user/activities#profile");
          }
        }
      },
      immediate: true,
    },
  },
};
</script>
